// pages/Agents/AgentSpace/AgentSpaceChat.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Avatar,
    CircularProgress,
    Snackbar,
    Alert,
    Chip,
    Tooltip,
    Dialog,
    DialogContent,
    Popover,
    TextField,
    MenuItem,
    Button,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PollIcon from '@mui/icons-material/Poll';
import PeopleIcon from '@mui/icons-material/People';
import ChatIcon from '@mui/icons-material/Chat';
import EventIcon from '@mui/icons-material/Event';
import EmailIcon from '@mui/icons-material/Email';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LinkIcon from '@mui/icons-material/Link';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { useNavigate, useLocation } from 'react-router-dom';
import AgentSpaceMessageBubble from './AgentSpaceMessageBubble';
import axiosInstance from '../../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    textTertiary: '#94a3b8',
    accent: '#8b5cf6',
    success: '#059669',
    danger: '#dc2626',
};

// Map icon_name from backend to React component
const ICON_MAP = {
    PollIcon: (size) => <PollIcon sx={{ fontSize: size }} />,
    PeopleIcon: (size) => <PeopleIcon sx={{ fontSize: size }} />,
    ChatIcon: (size) => <ChatIcon sx={{ fontSize: size }} />,
    EventIcon: (size) => <EventIcon sx={{ fontSize: size }} />,
    MailOutlineIcon: (size) => <MailOutlineIcon sx={{ fontSize: size }} />,
    EmailIcon: (size) => <EmailIcon sx={{ fontSize: size }} />,
    CalendarMonthIcon: (size) => <CalendarMonthIcon sx={{ fontSize: size }} />,
    CleaningServicesIcon: (size) => <CleaningServicesIcon sx={{ fontSize: size }} />,
};

function getIcon(iconName, size = 20) {
    const iconFn = ICON_MAP[iconName];
    return iconFn ? iconFn(size) : <AutoAwesomeIcon sx={{ fontSize: size }} />;
}

// Fallback agent types when API unavailable
const FALLBACK_AGENT_TYPES = [
    { id: 'survey', label: 'AI Survey', icon_name: 'PollIcon', color: '#3b82f6', description: 'Ask about survey responses & sentiments', capability_description: 'Ask to list surveys, view responses, run sentiment analysis on responses, see analytics charts, and filter responses by positive/negative sentiment. You can also email survey results.', requires_connection: false, requires_business: true },
    { id: 'crm', label: 'AI CRM', icon_name: 'PeopleIcon', color: '#f59e0b', description: 'Ask about leads, contacts & tickets', capability_description: 'Ask to view or update leads, contacts, opportunities, and tickets. You can search by name or email, update fields, and send emails to contacts directly from the conversation.', requires_connection: false, requires_business: true },
    { id: 'chat_history', label: 'AI Chat History', icon_name: 'ChatIcon', color: '#6366f1', description: 'Ask about chat sessions & feedback', capability_description: 'Ask to view chat conversations, read user feedback, run sentiment analysis on chats or feedback, see analytics, and filter sessions by sentiment. Great for understanding customer interactions.', requires_connection: false, requires_business: false },
    { id: 'events', label: 'AI Events', icon_name: 'EventIcon', color: '#059669', description: 'Ask to manage events & volunteers', capability_description: 'Ask to create, update, or view events. Manage volunteers â€” approve, assign to events, or send invites. Run outreach campaigns to invite attendees via email.', requires_connection: false, requires_business: true },
    { id: 'gmail', label: 'AI Gmail', icon_name: 'MailOutlineIcon', color: '#dc2626', description: 'Ask to read, search & send emails', capability_description: 'Ask to search your inbox, read specific emails, count unread messages, or send new emails. Just describe what you need â€” e.g. "show my emails from today" or "send a reply to John".', requires_connection: true, requires_business: false },
    { id: 'outlook', label: 'AI Outlook', icon_name: 'EmailIcon', color: '#0078d4', description: 'Ask to read, search & send emails', capability_description: 'Ask to search your Outlook inbox, read specific emails, count messages, or send new emails. Just describe what you need â€” e.g. "find emails about the project" or "reply to Sarah".', requires_connection: true, requires_business: false },
    { id: 'gmail-calendar', label: 'AI Calendar', icon_name: 'CalendarMonthIcon', color: '#0891b2', description: 'Ask to manage calendar events', capability_description: 'Ask to view upcoming events, create new meetings, reschedule or cancel events. Just say what you need â€” e.g. "schedule a meeting tomorrow at 3pm" or "what do I have this week".', requires_connection: true, requires_business: false },
    { id: 'gmail_cleanup', label: 'AI Gmail Cleanup', icon_name: 'CleaningServicesIcon', color: '#7c3aed', description: 'Clean inbox, summarize threads & build KB', capability_description: 'Removes newsletters, past event emails, and offers/ads. Creates summaries of conversation threads (group and one-on-one). Builds knowledge base documents from important emails. Schedule recurring cleanups.', requires_connection: true, requires_business: false, connection_type: 'gmail' },
    { id: 'general', label: 'AI General', icon_name: 'AutoAwesomeIcon', color: '#a855f7', description: 'Bring your own data — Excel, CSV, drive files', capability_description: 'Connect any data source (Excel, CSV, TXT, Word docs, Google Drive, OneDrive). Ask the agent to read columns, filter rows, summarize, and take actions like sending emails based on what it finds. Configure the source under agent settings.', requires_connection: false, requires_business: false },
];

// The first "User Asks" chip is always this fixed action — it tells the General
// agent to read the saved prompt and run the whole input→output task. Kept in
// sync (exact string) with the backend so it's never duplicated.
const RUN_TASK_QUESTION = 'Run the configured task';

export default function AgentSpaceChat({ sessionCode, onSessionChange, chatbotId, createTaskOpen: createTaskOpenProp, setCreateTaskOpen: setCreateTaskOpenProp, initialAgentType = null, onScheduledTaskCreated, onMessageStarted, onMessageComplete }) {
    const navigate = useNavigate();
    const location = useLocation();

    // Dynamic agent types from backend (business-account filtered)
    const [agentTypes, setAgentTypes] = useState([]);
    const [loadingAgentTypes, setLoadingAgentTypes] = useState(false);

    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [input, setInput] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

    // Agent selection state. If a parent pre-selects an agent (e.g. user picked
    // one from the landing list and then opened the FAB), seed the state so the
    // chat shows the right agent on first paint instead of flickering through
    // the "pick agent" empty state.
    const [selectedAgentType, setSelectedAgentType] = useState(initialAgentType || null);
    const [selectedResources, setSelectedResources] = useState([]);
    const [availableResources, setAvailableResources] = useState([]);
    // "User Asks" suggestions for the General agent (generated from its prompt).
    const [generalQuestions, setGeneralQuestions] = useState([]);
    // Whether the General agent has a runnable configured task (output prompt).
    const [hasConfiguredTask, setHasConfiguredTask] = useState(false);
    // "User Asks" starter prompts for non-general agents — their curated
    // example_prompts from the capabilities endpoint, shown as one-click chips.
    const [agentExamplePrompts, setAgentExamplePrompts] = useState([]);
    const [loadingResources, setLoadingResources] = useState(false);
    // Wizard steps:
    // 1 = pick agent
    // 2 = (connection agents) connect account  OR  (resource agents) agent info
    // 3 = (connection agents) prompt settings   OR  (resource agents) resource selection
    const [wizardStep, setWizardStep] = useState(initialAgentType ? 3 : 1);

    // Create task dialog - use props from parent, fallback to local state
    const [localCreateTaskOpen, setLocalCreateTaskOpen] = useState(false);
    const createTaskOpen = createTaskOpenProp !== undefined ? createTaskOpenProp : localCreateTaskOpen;
    const setCreateTaskOpen = setCreateTaskOpenProp || setLocalCreateTaskOpen;

    // Agent Space connection state (new â€” separate from old agent config)
    const [agentSpaceConnections, setAgentSpaceConnections] = useState({}); // { gmail: { is_connected, connected_email }, ... }
    const [connectingAgent, setConnectingAgent] = useState(false);
    const [disconnectingAgent, setDisconnectingAgent] = useState(false);

    // Agent Space prompt state
    const [agentPrompt, setAgentPrompt] = useState('');
    const [defaultPrompt, setDefaultPrompt] = useState('');
    const [isDefaultPrompt, setIsDefaultPrompt] = useState(true);
    const [savingPrompt, setSavingPrompt] = useState(false);
    const [loadingPrompt, setLoadingPrompt] = useState(false);
    const [promptEditing, setPromptEditing] = useState(false);

    const [internalSessionCode, setInternalSessionCode] = useState(null);
    // Use the parent-provided sessionCode only when it's an actual code.
    // When the parent passes `null` (e.g. AgentSpaceFAB on first mount),
    // fall back to the internal session so subsequent messages reuse the
    // same conversation instead of opening a fresh one on every send.
    const currentSessionCode = sessionCode || internalSessionCode;

    // Schedule-task popover state (clock icon next to Send)
    const [scheduleAnchorEl, setScheduleAnchorEl] = useState(null);
    const [scheduleFrequency, setScheduleFrequency] = useState('once');
    const [scheduleDateTime, setScheduleDateTime] = useState(() => {
        const d = new Date(Date.now() + 60 * 60 * 1000); // default +1h
        d.setSeconds(0, 0);
        // datetime-local needs YYYY-MM-DDTHH:mm
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    });
    // "Every N (minutes|hours)" interval for minutely/hourly frequencies.
    // Defaults to 15 (minutes) / 1 (hour) when the user picks each option.
    const [scheduleInterval, setScheduleInterval] = useState(15);
    const [scheduleSubmitting, setScheduleSubmitting] = useState(false);

    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);

    const isEmptyChat = messages.length === 0;

    // Fetch agent types from backend (business-account filtered)
    useEffect(() => {
        if (!chatbotId) return;
        let cancelled = false;
        setLoadingAgentTypes(true);
        axiosInstance.get('/api/agent-space/agent-types')
            .then(res => {
                if (!cancelled && res.data.success) {
                    setAgentTypes(res.data.data);
                }
            })
            .catch(() => {})
            .finally(() => { if (!cancelled) setLoadingAgentTypes(false); });
        return () => { cancelled = true; };
    }, [chatbotId]);

    // Fetch Agent Space connection statuses on mount
    useEffect(() => {
        if (!chatbotId) return;
        fetchAgentSpaceConnections();
    }, [chatbotId]);

    // Handle OAuth callback return (query params like ?agent_connected=gmail)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const connectedAgent = params.get('agent_connected');
        const errorAgent = params.get('agent_error');

        if (connectedAgent) {
            // Auto-select the agent and advance to prompt step
            setSelectedAgentType(connectedAgent);
            setWizardStep(3); // Go to prompt settings
            fetchAgentSpaceConnections();
            setSnackbar({ open: true, message: `Successfully connected ${connectedAgent}!`, severity: 'success' });
            // Clean URL
            navigate(location.pathname, { replace: true });
        } else if (errorAgent) {
            const errorMsg = params.get('error') || 'Connection failed';
            setSelectedAgentType(errorAgent);
            setWizardStep(2); // Stay on connection step
            setSnackbar({ open: true, message: `Failed to connect ${errorAgent}: ${errorMsg}`, severity: 'error' });
            navigate(location.pathname, { replace: true });
        }
    }, [location.search]);

    const fetchAgentSpaceConnections = async () => {
        try {
            const resp = await axiosInstance.get(`/api/agent-space/connections?chatbot_id=${chatbotId}`);
            if (resp.data?.success) {
                setAgentSpaceConnections(resp.data.data || {});
            }
        } catch (e) {
            console.error('Failed to fetch Agent Space connections:', e);
        }
    };

    // When dialog opens from parent (e.g. sidebar New Task), ensure wizard is at step 1 if no agent selected
    useEffect(() => {
        if (createTaskOpen && !selectedAgentType) {
            setWizardStep(1);
        }
    }, [createTaskOpen]);

    // Load conversation when session code changes
    useEffect(() => {
        if (currentSessionCode) {
            loadConversation(currentSessionCode);
        } else {
            setMessages([]);
            setSelectedAgentType(initialAgentType || null);
            setSelectedResources([]);
            setAvailableResources([]);
            setWizardStep(initialAgentType ? 3 : 1);
        }
    }, [currentSessionCode, initialAgentType]);

    // Auto-scroll on new messages
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isSending]);

    // Auto-resize textarea
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
        }
    }, [input]);

    // Derive agent capabilities from fetched data
    const isAgentWithResources = (id) => {
        const agent = agentTypes.find(a => a.id === id);
        return agent && !agent.requires_connection;
    };
    const isAgentRequiringConnection = (id) => {
        const agent = agentTypes.find(a => a.id === id);
        return agent?.requires_connection || false;
    };

    // Load resources when agent type changes (only for resource-based agents)
    useEffect(() => {
        if (selectedAgentType && chatbotId && isAgentWithResources(selectedAgentType)) {
            loadResources(selectedAgentType);
        } else {
            setAvailableResources([]);
            setSelectedResources([]);
        }
    }, [selectedAgentType, chatbotId, agentTypes]);

    // Load prompt when agent type changes and on connection step completion
    useEffect(() => {
        if (selectedAgentType && chatbotId && wizardStep === 3 && isAgentRequiringConnection(selectedAgentType)) {
            loadAgentPrompt(selectedAgentType);
        }
    }, [selectedAgentType, chatbotId, wizardStep, agentTypes]);

    // Load the General agent's "User Asks" suggestions (generated from its
    // saved prompt) so we can show them above the input as one-click starters.
    useEffect(() => {
        if (selectedAgentType !== 'general' || !chatbotId) {
            setGeneralQuestions([]);
            setHasConfiguredTask(false);
            return;
        }
        let cancelled = false;
        axiosInstance.get(`/api/agent-space/general/source?chatbot_id=${chatbotId}`)
            .then((res) => {
                if (cancelled) return;
                const out = res.data?.data?.output;
                const q = out?.example_questions;
                setGeneralQuestions(Array.isArray(q) ? q : []);
                setHasConfiguredTask(!!(out && out.enabled && (out.prompt || '').trim()));
            })
            .catch(() => { if (!cancelled) { setGeneralQuestions([]); setHasConfiguredTask(false); } });
        return () => { cancelled = true; };
    }, [selectedAgentType, chatbotId]);

    // Load "User Asks" for non-general agents: the AI-generated example_questions
    // (saved on the unified prompt record) if present, else the curated
    // example_prompts. Shown as the same one-click starter chips as General.
    useEffect(() => {
        if (!selectedAgentType || selectedAgentType === 'general' || !chatbotId) {
            setAgentExamplePrompts([]);
            return undefined;
        }
        let cancelled = false;
        axiosInstance.get(`/api/agent-space/prompts/${selectedAgentType}?chatbot_id=${chatbotId}`)
            .then((res) => {
                if (cancelled) return;
                const d = res.data?.data || {};
                const generated = Array.isArray(d.example_questions) ? d.example_questions : [];
                const curated = Array.isArray(d.example_prompts) ? d.example_prompts : [];
                setAgentExamplePrompts(generated.length ? generated : curated);
            })
            .catch(() => { if (!cancelled) setAgentExamplePrompts([]); });
        return () => { cancelled = true; };
    }, [selectedAgentType, chatbotId]);

    const loadConversation = async (code) => {
        setIsLoading(true);
        try {
            const resp = await axiosInstance.get(`/api/agent-space/conversations/${code}/messages`);
            if (resp.data.success) {
                const { conversation, messages: msgs } = resp.data.data;
                setMessages(msgs.map(m => ({
                    ...m,
                    is_from_user: m.role === 'USER',
                })));
                if (conversation.agent_type) {
                    setSelectedAgentType(conversation.agent_type);
                }
                if (conversation.selected_resources) {
                    setSelectedResources(conversation.selected_resources);
                }
            }
        } catch (error) {
            console.error('Failed to load conversation:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const loadResources = async (agentType) => {
        if (isAgentRequiringConnection(agentType)) {
            setAvailableResources([]);
            return;
        }
        setLoadingResources(true);
        try {
            const resp = await axiosInstance.get(`/api/agent-space/resources/${agentType}?chatbot_id=${chatbotId}`);
            if (resp.data.success) {
                const data = resp.data.data;
                if (Array.isArray(data)) {
                    setAvailableResources(data);
                } else {
                    const flat = [];
                    Object.entries(data).forEach(([key, items]) => {
                        items.forEach(item => flat.push({ ...item, resourceType: key }));
                    });
                    setAvailableResources(flat);
                }
            }
        } catch (error) {
            console.error('Failed to load resources:', error);
        } finally {
            setLoadingResources(false);
        }
    };

    const loadAgentPrompt = async (agentType) => {
        setLoadingPrompt(true);
        try {
            const resp = await axiosInstance.get(`/api/agent-space/prompts/${agentType}?chatbot_id=${chatbotId}`);
            if (resp.data.success) {
                const { prompt, is_default, default_prompt } = resp.data.data;
                setAgentPrompt(prompt || '');
                setDefaultPrompt(default_prompt || '');
                setIsDefaultPrompt(is_default);
                setPromptEditing(false);
            }
        } catch (error) {
            console.error('Failed to load agent prompt:', error);
        } finally {
            setLoadingPrompt(false);
        }
    };

    // --- Connection Handlers ---
    const handleConnect = async (agentType) => {
        setConnectingAgent(true);
        try {
            const resp = await axiosInstance.get(`/api/agent-space/connections/${agentType}/auth/start?chatbot_id=${chatbotId}`);
            if (resp.data?.success && resp.data?.authorization_url) {
                window.location.href = resp.data.authorization_url;
            } else {
                setSnackbar({ open: true, message: resp.data?.message || 'Failed to start connection', severity: 'error' });
                setConnectingAgent(false);
            }
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || 'Connection failed', severity: 'error' });
            setConnectingAgent(false);
        }
    };

    const handleDisconnect = async (agentType) => {
        setDisconnectingAgent(true);
        try {
            await axiosInstance.post(`/api/agent-space/connections/${agentType}/disconnect?chatbot_id=${chatbotId}`);
            setAgentSpaceConnections(prev => {
                const updated = { ...prev };
                delete updated[agentType];
                return updated;
            });
            setSnackbar({ open: true, message: `Disconnected ${agentType} successfully`, severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: 'Failed to disconnect', severity: 'error' });
        } finally {
            setDisconnectingAgent(false);
        }
    };

    // --- Prompt Handlers ---
    const handleSavePrompt = async () => {
        setSavingPrompt(true);
        try {
            await axiosInstance.post(`/api/agent-space/prompts/${selectedAgentType}?chatbot_id=${chatbotId}`, {
                prompt: agentPrompt,
            });
            setIsDefaultPrompt(false);
            setPromptEditing(false);
            setSnackbar({ open: true, message: 'Prompt saved successfully', severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: 'Failed to save prompt', severity: 'error' });
        } finally {
            setSavingPrompt(false);
        }
    };

    const handleResetPrompt = async () => {
        setSavingPrompt(true);
        try {
            const resp = await axiosInstance.delete(`/api/agent-space/prompts/${selectedAgentType}?chatbot_id=${chatbotId}`);
            if (resp.data.success) {
                const { prompt, default_prompt } = resp.data.data;
                setAgentPrompt(prompt || default_prompt || '');
                setDefaultPrompt(default_prompt || '');
                setIsDefaultPrompt(true);
                setPromptEditing(false);
                setSnackbar({ open: true, message: 'Prompt reset to default', severity: 'success' });
            }
        } catch (err) {
            setSnackbar({ open: true, message: 'Failed to reset prompt', severity: 'error' });
        } finally {
            setSavingPrompt(false);
        }
    };

    // --- Agent Selection ---
    const handleSelectAgent = (agentType) => {
        if (currentSessionCode && messages.length > 0) {
            // In an existing conversation, just update the agent type
        }
        setSelectedAgentType(agentType);
        setSelectedResources([]);
        setPromptEditing(false);

        if (isAgentRequiringConnection(agentType)) {
            setWizardStep(2); // Go to connection step
        } else {
            setWizardStep(2); // Go to agent info step (same number, different content)
        }
    };

    const handleBackToAgents = () => {
        setSelectedAgentType(null);
        setSelectedResources([]);
        setAvailableResources([]);
        setAgentPrompt('');
        setDefaultPrompt('');
        setIsDefaultPrompt(true);
        setPromptEditing(false);
        setWizardStep(1);
    };

    // Close dialog and keep selection (used when user is done selecting)
    const handleCloseCreateTask = () => {
        setCreateTaskOpen(false);
    };

    // Open create task dialog
    const handleOpenCreateTask = () => {
        setCreateTaskOpen(true);
        if (!selectedAgentType) {
            setWizardStep(1);
        } else if (isAgentWithResources(selectedAgentType) && availableResources.length > 0) {
            setWizardStep(3); // Go directly to resource selection
        }
    };

    const handleToggleResource = (resource, fromDialog = false) => {
        setSelectedResources(prev => {
            const exists = prev.find(r => r.id === resource.id && r.type === (resource.resourceType || resource.type));
            if (exists) {
                return prev.filter(r => !(r.id === resource.id && r.type === (resource.resourceType || resource.type)));
            }
            return [...prev, { id: resource.id, label: resource.label, type: resource.resourceType || resource.type || selectedAgentType }];
        });
        // Close dialog when selecting from dialog so user sees it in message area
        if (fromDialog) {
            setCreateTaskOpen(false);
        }
    };

    const handleOpenSchedule = (e) => {
        if (!input.trim()) {
            setSnackbar({ open: true, message: 'Type a task first, then schedule it.', severity: 'warning' });
            return;
        }
        if (!selectedAgentType) {
            setSnackbar({ open: true, message: 'Select an agent type before scheduling.', severity: 'warning' });
            return;
        }
        setScheduleAnchorEl(e.currentTarget);
    };

    const handleSubmitSchedule = async () => {
        const text = input.trim();
        if (!text || !selectedAgentType || scheduleSubmitting) return;

        const payload = {
            chatbot_id: chatbotId,
            agent_type: selectedAgentType,
            task_message: text,
            frequency: scheduleFrequency,
        };
        // minutely / hourly run on a fixed cadence — N minutes/hours between
        // runs — and don't need the datetime anchor field.
        if (scheduleFrequency === 'minutely' || scheduleFrequency === 'hourly') {
            const n = parseInt(scheduleInterval, 10);
            if (!n || n < 1) {
                setSnackbar({ open: true, message: 'Set how often the task should run.', severity: 'warning' });
                return;
            }
            payload.interval_value = n;
        } else if (scheduleDateTime) {
            // For 'once' it's the single run time; for daily/weekly/monthly it
            // anchors the cadence (time of day, day of week, day of month).
            payload.scheduled_at = new Date(scheduleDateTime).toISOString();
        }

        setScheduleSubmitting(true);
        try {
            const resp = await axiosInstance.post('/api/agent-space/scheduled-tasks', payload);
            if (resp.data?.success) {
                setSnackbar({ open: true, message: 'Task scheduled.', severity: 'success' });
                setInput('');
                setScheduleAnchorEl(null);
                if (onScheduledTaskCreated) onScheduledTaskCreated(resp.data.data);
            } else {
                setSnackbar({ open: true, message: resp.data?.message || 'Failed to schedule task', severity: 'error' });
            }
        } catch (e) {
            console.error('Failed to schedule task:', e);
            setSnackbar({ open: true, message: 'Failed to schedule task.', severity: 'error' });
        } finally {
            setScheduleSubmitting(false);
        }
    };

    const handleSend = async (overrideText = null) => {
        // Called both from the Send button (gets a click event) and from a
        // "User Asks" chip (gets the question string). Only a string overrides.
        const text = (typeof overrideText === 'string' ? overrideText : input).trim();
        if (!text || isSending) return;
        if (!selectedAgentType) {
            setSnackbar({ open: true, message: 'Please select an agent type first', severity: 'warning' });
            return;
        }

        const userMsg = {
            id: `user-${Date.now()}`,
            role: 'USER',
            is_from_user: true,
            message: text,
            metadata: { agent_type: selectedAgentType, selected_resources: selectedResources },
            created_at: new Date().toISOString(),
        };

        setMessages(prev => [...prev, userMsg]);
        setInput('');
        setIsSending(true);
        // Notify the parent so it can start polling the activity feed while
        // the request is in flight (steps stream into the DB incrementally).
        if (onMessageStarted) {
            onMessageStarted({ session_code: currentSessionCode || null });
        }

        let completedPayload = null;
        try {
            const resp = await axiosInstance.post('/api/agent-space/send-message', {
                message: text,
                session_code: currentSessionCode,
                chatbot_id: chatbotId,
                agent_type: selectedAgentType,
                selected_resources: selectedResources,
            });

            if (resp.data.success) {
                const { session_code, agent_response, is_new_conversation } = resp.data.data;

                if (is_new_conversation || !currentSessionCode) {
                    setInternalSessionCode(session_code);
                    if (onSessionChange) onSessionChange(session_code);
                }

                setMessages(prev => [...prev, {
                    ...agent_response,
                    is_from_user: false,
                }]);
                completedPayload = { session_code, is_new_conversation };
            } else {
                setSnackbar({ open: true, message: resp.data.message || 'Failed to send message', severity: 'error' });
                completedPayload = { error: resp.data.message };
            }
        } catch (error) {
            console.error('Error sending message:', error);
            setSnackbar({ open: true, message: 'Failed to send message. Please try again.', severity: 'error' });
            completedPayload = { error: error?.message };
        } finally {
            setIsSending(false);
            if (onMessageComplete) onMessageComplete(completedPayload || {});
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const agentInfo = agentTypes.find(a => a.id === selectedAgentType);
    const isConnectionAgent = selectedAgentType && isAgentRequiringConnection(selectedAgentType);
    // Resolve connection type: some agents (e.g. gmail_cleanup) reuse another agent's connection
    const resolvedConnectionType = agentInfo?.connection_type || selectedAgentType;
    const connectionData = selectedAgentType ? agentSpaceConnections[resolvedConnectionType] : null;
    const isAgentConnected = connectionData?.is_connected || false;

    // Starter "User Asks" chips shown above the composer on an empty chat.
    // General → its run-task action + AI-generated questions; every other
    // agent → its curated example_prompts. One click sends the prompt.
    const isGeneralAgent = selectedAgentType === 'general';
    // "Run the configured task" is always the first chip for EVERY agent — it
    // tells the agent to execute its configured prompt/task — followed by the
    // generated/curated questions.
    const suggestionChips = isGeneralAgent
        ? ((hasConfiguredTask || generalQuestions.length > 0)
            ? [RUN_TASK_QUESTION, ...generalQuestions.filter((q) => q !== RUN_TASK_QUESTION)]
            : [])
        : (selectedAgentType
            ? [RUN_TASK_QUESTION, ...agentExamplePrompts.filter((q) => q !== RUN_TASK_QUESTION)]
            : []);
    // Hide the chips when the user can't act on them yet (a connection agent
    // that hasn't been connected — the composer is disabled in that state).
    const canUseComposerNow = !(isConnectionAgent && !isAgentConnected);

    // --- Back button component ---
    const BackButton = ({ onClick, label = 'Back' }) => (
        <Box
            onClick={onClick}
            sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
                cursor: 'pointer', alignSelf: 'flex-start', ml: 2,
                color: colors.textTertiary, '&:hover': { color: colors.accent },
                transition: 'color 0.2s',
            }}
        >
            <ArrowBackIcon sx={{ fontSize: 16 }} />
            <Typography sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 500 }}>
                {label}
            </Typography>
        </Box>
    );

    // --- Agent badge component ---
    const AgentBadge = () => agentInfo ? (
        <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            px: 3, py: 2, borderRadius: '16px',
            bgcolor: `${agentInfo.color}10`,
            border: `2px solid ${agentInfo.color}`,
        }}>
            <Box sx={{ color: agentInfo.color, display: 'flex' }}>
                {getIcon(agentInfo.icon_name, 28)}
            </Box>
            <Box>
                <Typography sx={{ fontFamily, fontSize: '1rem', fontWeight: 700, color: colors.textPrimary }}>
                    {agentInfo.label} Agent
                </Typography>
                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: colors.textSecondary, maxWidth: 360 }}>
                    {agentInfo.capability_description}
                </Typography>
            </Box>
        </Box>
    ) : null;

    return (
        <Box sx={{
            display: 'flex', flexDirection: 'column', height: '100%',
            bgcolor: 'rgba(255,255,255,0.5)', backdropFilter: 'blur(12px)',
            borderRadius: '16px', border: '1px solid rgba(0,0,0,0.06)',
            overflow: 'hidden', fontFamily,
        }}>
            {/* Messages Area */}
            <Box sx={{
                flex: 1, overflow: 'auto', p: { xs: 1.5, sm: 3 },
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.1)', borderRadius: '6px' },
            }}>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <CircularProgress size={32} sx={{ color: colors.accent }} />
                    </Box>
                ) : isEmptyChat ? (
                    /* Empty State - introduction message describing what the
                       selected agent can do. Scales up nicely so it doesn't
                       feel lost in the expanded (2/3-width) chat view. */
                    <Box sx={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        justifyContent: 'center', height: '100%', gap: 2.5,
                        px: 3, textAlign: 'center',
                    }}>
                        {selectedAgentType && agentInfo ? (
                            <>
                                {/* Agent-colored icon */}
                                <Box sx={{
                                    width: { xs: 56, sm: 72 }, height: { xs: 56, sm: 72 },
                                    borderRadius: { xs: '18px', sm: '22px' },
                                    background: `linear-gradient(135deg, ${agentInfo.color}, ${agentInfo.color}cc)`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    boxShadow: `0 12px 32px ${agentInfo.color}40`,
                                    color: '#fff',
                                }}>
                                    {getIcon(agentInfo.icon_name, 34)}
                                </Box>

                                {/* Welcome heading */}
                                <Box sx={{ maxWidth: 520 }}>
                                    <Typography sx={{
                                        fontFamily, fontSize: { xs: '1rem', sm: '1.25rem' },
                                        fontWeight: 700, color: colors.textPrimary, mb: 0.75,
                                        letterSpacing: '-0.01em',
                                    }}>
                                        Hi! I'm your {agentInfo.label} agent.
                                    </Typography>
                                    <Typography sx={{
                                        fontFamily, fontSize: { xs: '0.82rem', sm: '0.9rem' },
                                        color: colors.textSecondary, lineHeight: 1.6,
                                    }}>
                                        {agentInfo.capability_description || agentInfo.description || 'Type a task below to get started.'}
                                    </Typography>
                                </Box>

                                <Typography sx={{
                                    fontFamily, fontSize: '0.74rem', color: colors.textTertiary,
                                    mt: 0.5,
                                }}>
                                    Type your task below — try "show me…", "filter…", or "send…"
                                </Typography>

                                {selectedResources.length > 0 && (
                                    <Typography sx={{ fontFamily, fontSize: '0.7rem', color: colors.textTertiary }}>
                                        {selectedResources.length} resource{selectedResources.length !== 1 ? 's' : ''} selected
                                    </Typography>
                                )}
                            </>
                        ) : (
                            <>
                                {/* No agent yet (rare â€” the FAB normally pre-selects) */}
                                <Box sx={{
                                    width: { xs: 44, sm: 56 }, height: { xs: 44, sm: 56 },
                                    borderRadius: { xs: '14px', sm: '18px' },
                                    background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    boxShadow: '0 8px 24px rgba(139,92,246,0.25)',
                                }}>
                                    <AutoAwesomeIcon sx={{ color: '#fff', fontSize: { xs: 22, sm: 28 } }} />
                                </Box>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography sx={{ fontFamily, fontSize: { xs: '1rem', sm: '1.1rem' }, fontWeight: 700, color: colors.textPrimary, mb: 0.5 }}>
                                        What can I help you with?
                                    </Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.78rem', color: colors.textTertiary, maxWidth: 320 }}>
                                        Create a task to get started.
                                    </Typography>
                                </Box>
                                <Box
                                    onClick={handleOpenCreateTask}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: 1,
                                        fontFamily, fontSize: '0.85rem', fontWeight: 600,
                                        color: '#fff', cursor: 'pointer',
                                        px: 3, py: 1.25, borderRadius: '12px',
                                        background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                                        boxShadow: '0 4px 16px rgba(139,92,246,0.3)',
                                        transition: 'all 0.2s ease',
                                        '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 6px 20px rgba(139,92,246,0.4)' },
                                    }}
                                >
                                    <AddCircleOutlineIcon sx={{ fontSize: 20 }} />
                                    Create Task
                                </Box>
                            </>
                        )}
                    </Box>
                ) : (
                    /* Messages */
                    <>
                        {messages.map((msg) => (
                            <AgentSpaceMessageBubble key={msg.id} msg={msg} />
                        ))}
                        {isSending && (
                            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                <Avatar sx={{
                                    width: 32, height: 32,
                                    background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                                }}>
                                    <AutoAwesomeIcon sx={{ fontSize: 18 }} />
                                </Avatar>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pt: 1 }}>
                                    {[0, 150, 300].map((delay) => (
                                        <Box
                                            key={delay}
                                            sx={{
                                                width: 8, height: 8, borderRadius: '50%',
                                                bgcolor: colors.accent, opacity: 0.6,
                                                animation: 'bounce 1.4s infinite ease-in-out both',
                                                animationDelay: `${delay}ms`,
                                                '@keyframes bounce': {
                                                    '0%, 80%, 100%': { transform: 'scale(0)' },
                                                    '40%': { transform: 'scale(1)' },
                                                },
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}
                        <div ref={messagesEndRef} />
                    </>
                )}
            </Box>

            {/* Bottom Input Area */}
            <Box sx={{ px: { xs: 1, sm: 2 }, pb: { xs: 1, sm: 2 }, pt: { xs: 1, sm: 1.5 }, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                {/* Agent picker chips removed â€” the FAB header already shows
                    "Chat with [agent]" so the chip strip was redundant. */}

                {/* Input Box with inline resources */}
                <Box sx={{
                    bgcolor: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(12px)',
                    borderRadius: '14px', border: '1px solid rgba(0,0,0,0.08)',
                    transition: 'border-color 0.2s',
                    '&:focus-within': { borderColor: `${colors.accent}60` },
                    overflow: 'hidden',
                }}>
                    {/* Inline: Resource chips + connection info (no agent
                        badge â€” the FAB header already shows the agent name). */}
                    {selectedAgentType && (
                        <Box sx={{ px: 1.5, pt: 1.25, pb: 0.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            {/* Helper row: resource count + connected email */}
                            {(selectedResources.length > 0
                                || (!isConnectionAgent && isAgentWithResources(selectedAgentType) && availableResources.length > 0)
                                || (isConnectionAgent && isAgentConnected && connectionData?.connected_email)) && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                    {selectedResources.length > 0 && (
                                        <Typography sx={{ fontFamily, fontSize: '0.65rem', color: colors.textTertiary }}>
                                            {selectedResources.length} resource{selectedResources.length !== 1 ? 's' : ''} selected
                                        </Typography>
                                    )}
                                    {!isConnectionAgent && isAgentWithResources(selectedAgentType) && availableResources.length > 0 && (
                                        <Tooltip title="Select resources">
                                            <IconButton
                                                size="small"
                                                onClick={handleOpenCreateTask}
                                                sx={{
                                                    width: 22, height: 22,
                                                    color: agentInfo?.color || colors.accent,
                                                    '&:hover': { bgcolor: `${agentInfo?.color || colors.accent}10` },
                                                }}
                                            >
                                                <AddCircleOutlineIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {isConnectionAgent && isAgentConnected && connectionData?.connected_email && (
                                        <Typography sx={{ fontFamily, fontSize: '0.6rem', color: colors.success }}>
                                            {connectionData.connected_email}
                                        </Typography>
                                    )}
                                </Box>
                            )}

                            {/* Resource chips row */}
                            {!isConnectionAgent && availableResources.length > 0 && (
                                <Box sx={{
                                    display: 'flex', flexWrap: 'wrap', gap: 0.5,
                                    maxHeight: 64, overflow: 'auto',
                                    '&::-webkit-scrollbar': { height: '3px' },
                                    '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.08)', borderRadius: '4px' },
                                }}>
                                    {loadingResources ? (
                                        <CircularProgress size={14} sx={{ color: colors.accent }} />
                                    ) : (
                                        availableResources.map((resource) => {
                                            const isSelected = selectedResources.some(r => r.id === resource.id && r.type === (resource.resourceType || resource.type));
                                            return (
                                                <Chip
                                                    key={`${resource.id}-${resource.resourceType || ''}`}
                                                    label={resource.label}
                                                    onClick={() => handleToggleResource(resource)}
                                                    size="small"
                                                    disabled={isSending}
                                                    sx={{
                                                        fontFamily, fontSize: '0.6rem', height: 24,
                                                        bgcolor: isSelected ? `${agentInfo?.color || colors.accent}12` : 'transparent',
                                                        border: `1px solid ${isSelected ? (agentInfo?.color || colors.accent) : 'rgba(0,0,0,0.1)'}`,
                                                        color: isSelected ? (agentInfo?.color || colors.accent) : colors.textSecondary,
                                                        fontWeight: isSelected ? 600 : 400,
                                                        '&:hover': { bgcolor: `${agentInfo?.color || colors.accent}08` },
                                                    }}
                                                />
                                            );
                                        })
                                    )}
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* "User Asks" starter chips (all agents) — one click sends the prompt. */}
                    {isEmptyChat && canUseComposerNow && suggestionChips.length > 0 && (
                        <Box sx={{ px: 1.5, pt: selectedAgentType ? 0 : 1.25, pb: 0.75 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.6 }}>
                                <AutoAwesomeIcon sx={{ fontSize: 13, color: colors.accent }} />
                                <Typography sx={{ fontFamily, fontSize: '0.6rem', fontWeight: 700, color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                    Suggested Questions
                                </Typography>
                            </Box>
                            <Box sx={{
                                display: 'flex', flexWrap: 'wrap', gap: 0.5,
                                maxHeight: 92, overflow: 'auto',
                                '&::-webkit-scrollbar': { height: '3px', width: '3px' },
                                '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.08)', borderRadius: '4px' },
                            }}>
                                {suggestionChips.map((q, i) => {
                                    const primary = i === 0;
                                    return (
                                        <Chip
                                            key={i}
                                            label={q}
                                            onClick={() => handleSend(q)}
                                            disabled={isSending}
                                            icon={primary
                                                ? <AutoAwesomeIcon sx={{ fontSize: '13px !important' }} />
                                                : <ChatBubbleOutlineIcon sx={{ fontSize: '13px !important' }} />}
                                            size="small"
                                            sx={{
                                                fontFamily, fontSize: '0.66rem', height: 26, maxWidth: '100%',
                                                fontWeight: primary ? 700 : 400,
                                                bgcolor: primary ? `${colors.accent}1f` : `${colors.accent}0d`,
                                                border: `1px solid ${primary ? `${colors.accent}55` : `${colors.accent}26`}`,
                                                color: primary ? colors.textPrimary : colors.textSecondary,
                                                cursor: 'pointer',
                                                '& .MuiChip-icon': { color: colors.accent, ml: '6px' },
                                                '&:hover': { bgcolor: primary ? `${colors.accent}2b` : `${colors.accent}18`, borderColor: `${colors.accent}44` },
                                            }}
                                        />
                                    );
                                })}
                            </Box>
                        </Box>
                    )}

                    {/* Textarea + Send row */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, px: { xs: 1, sm: 1.5 }, py: { xs: 1, sm: 1.25 } }}>
                        <textarea
                            ref={textareaRef}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={
                                !selectedAgentType
                                    ? 'Select an agent type above to get started...'
                                    : isConnectionAgent && !isAgentConnected
                                        ? `Connect your ${agentInfo?.label} account first...`
                                        : selectedResources.length > 0
                                            ? 'Describe your task... (e.g., "Analyze sentiments from this survey")'
                                            : 'Describe your task...'
                            }
                            disabled={isSending || (isConnectionAgent && !isAgentConnected && isEmptyChat)}
                            rows={1}
                            style={{
                                flex: 1, border: 'none', outline: 'none', resize: 'none',
                                fontFamily, fontSize: '0.85rem', lineHeight: '1.5',
                                color: colors.textPrimary, backgroundColor: 'transparent',
                                // overflowY auto only — no horizontal scroll
                                // arrows for browsers that show them when
                                // overflow:'auto' is set in both directions.
                                maxHeight: 200, overflowY: 'auto', overflowX: 'hidden',
                                padding: 0,
                            }}
                        />
                        <Tooltip title={!input.trim() ? 'Type a task first' : !selectedAgentType ? 'Select an agent type first' : 'Schedule for later'}>
                            <span>
                                <IconButton
                                    onClick={handleOpenSchedule}
                                    disabled={isSending || !input.trim() || !selectedAgentType || (isConnectionAgent && !isAgentConnected && isEmptyChat)}
                                    sx={{
                                        width: 36, height: 36,
                                        bgcolor: 'rgba(0,0,0,0.04)',
                                        color: input.trim() && selectedAgentType ? colors.accent : colors.textTertiary,
                                        '&:hover': { bgcolor: 'rgba(139,92,246,0.12)', color: colors.accent },
                                        '&.Mui-disabled': { color: colors.textTertiary, bgcolor: 'rgba(0,0,0,0.04)' },
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    <AccessTimeIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={!selectedAgentType ? 'Select an agent type first' : isConnectionAgent && !isAgentConnected ? `Connect ${agentInfo?.label} first` : 'Send'}>
                            <span>
                                <IconButton
                                    onClick={handleSend}
                                    disabled={isSending || !input.trim() || !selectedAgentType || (isConnectionAgent && !isAgentConnected && isEmptyChat)}
                                    sx={{
                                        width: 36, height: 36,
                                        background: input.trim() && selectedAgentType ? 'linear-gradient(135deg, #8b5cf6, #6d28d9)' : 'rgba(0,0,0,0.05)',
                                        color: input.trim() && selectedAgentType ? '#fff' : colors.textTertiary,
                                        '&:hover': { background: 'linear-gradient(135deg, #6d28d9, #8b5cf6)' },
                                        '&.Mui-disabled': { color: colors.textTertiary, background: 'rgba(0,0,0,0.05)' },
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    <SendIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>

                    {/* Schedule popover. The chatbox card is z-indexed at
                        1301 (above the info drawer), so the popover needs
                        to layer above it or the user can't see it. */}
                    <Popover
                        open={Boolean(scheduleAnchorEl)}
                        anchorEl={scheduleAnchorEl}
                        onClose={() => setScheduleAnchorEl(null)}
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        sx={{ zIndex: 1450 }}
                        slotProps={{ paper: { sx: { mt: -1, borderRadius: '14px', p: 2, width: 300, boxShadow: '0 12px 40px rgba(0,0,0,0.18)' } } }}
                        PaperProps={{ sx: { mt: -1, borderRadius: '14px', p: 2, width: 300, boxShadow: '0 12px 40px rgba(0,0,0,0.18)' } }}
                    >
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 700, color: colors.textPrimary, mb: 0.5 }}>
                            Schedule task
                        </Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.7rem', color: colors.textTertiary, mb: 1.5 }}>
                            Run the typed task on a schedule for this agent.
                        </Typography>
                        <TextField
                            select
                            size="small"
                            fullWidth
                            label="Frequency"
                            value={scheduleFrequency}
                            onChange={(e) => {
                                const next = e.target.value;
                                setScheduleFrequency(next);
                                // Seed a sensible default for the interval input
                                // when the user picks minutely / hourly the first
                                // time, so they only have to override if they want.
                                if (next === 'minutely' && (!scheduleInterval || scheduleInterval < 1)) {
                                    setScheduleInterval(15);
                                } else if (next === 'hourly' && (!scheduleInterval || scheduleInterval < 1)) {
                                    setScheduleInterval(1);
                                }
                            }}
                            InputProps={{ sx: { fontFamily, fontSize: '0.8rem' } }}
                            InputLabelProps={{ sx: { fontFamily, fontSize: '0.8rem' } }}
                            // The Select's dropdown menu portals to body and uses
                            // MUI's default modal z-index (1300), which sits below
                            // the chatbox card (1301) and the schedule popover
                            // (1450). Lift it above both so options are clickable.
                            SelectProps={{ MenuProps: { sx: { zIndex: 1500 } } }}
                            sx={{ mb: 1.5 }}
                        >
                            <MenuItem value="once" sx={{ fontFamily, fontSize: '0.8rem' }}>One-time</MenuItem>
                            <MenuItem value="minutely" sx={{ fontFamily, fontSize: '0.8rem' }}>Every N minutes</MenuItem>
                            <MenuItem value="hourly" sx={{ fontFamily, fontSize: '0.8rem' }}>Every N hours</MenuItem>
                            <MenuItem value="daily" sx={{ fontFamily, fontSize: '0.8rem' }}>Daily</MenuItem>
                            <MenuItem value="weekly" sx={{ fontFamily, fontSize: '0.8rem' }}>Weekly</MenuItem>
                            <MenuItem value="monthly" sx={{ fontFamily, fontSize: '0.8rem' }}>Monthly</MenuItem>
                        </TextField>
                        {/* For 'minutely' / 'hourly': how many minutes/hours
                            between runs. For other frequencies: anchor datetime
                            (when 'once', the run time; for daily/weekly/monthly,
                            the FIRST run that also sets the recurring cadence). */}
                        {(scheduleFrequency === 'minutely' || scheduleFrequency === 'hourly') ? (
                            <TextField
                                type="number"
                                size="small"
                                fullWidth
                                label={scheduleFrequency === 'minutely' ? 'Every N minutes' : 'Every N hours'}
                                value={scheduleInterval}
                                onChange={(e) => setScheduleInterval(e.target.value)}
                                inputProps={{ min: 1, step: 1, sx: { fontFamily, fontSize: '0.8rem' } }}
                                InputLabelProps={{ sx: { fontFamily, fontSize: '0.8rem' } }}
                                helperText={
                                    scheduleFrequency === 'minutely'
                                        ? 'e.g. 15 → runs every 15 minutes starting from now.'
                                        : 'e.g. 2 → runs every 2 hours starting from now.'
                                }
                                FormHelperTextProps={{ sx: { fontFamily, fontSize: '0.65rem', color: colors.textTertiary, mt: 0.25, ml: 0 } }}
                                sx={{ mb: 1.5 }}
                            />
                        ) : (
                            <TextField
                                type="datetime-local"
                                size="small"
                                fullWidth
                                label={scheduleFrequency === 'once' ? 'When' : 'First run'}
                                value={scheduleDateTime}
                                onChange={(e) => setScheduleDateTime(e.target.value)}
                                InputLabelProps={{ shrink: true, sx: { fontFamily, fontSize: '0.8rem' } }}
                                InputProps={{ sx: { fontFamily, fontSize: '0.8rem' } }}
                                helperText={
                                    scheduleFrequency === 'daily' ? 'Runs every day at this time.'
                                        : scheduleFrequency === 'weekly' ? 'Runs every week on this day at this time.'
                                        : scheduleFrequency === 'monthly' ? 'Runs every month on this date at this time.'
                                        : ' '
                                }
                                FormHelperTextProps={{ sx: { fontFamily, fontSize: '0.65rem', color: colors.textTertiary, mt: 0.25, ml: 0 } }}
                                sx={{ mb: 1.5 }}
                            />
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button
                                size="small"
                                onClick={() => setScheduleAnchorEl(null)}
                                sx={{ fontFamily, fontSize: '0.75rem', textTransform: 'none', color: colors.textTertiary }}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                onClick={handleSubmitSchedule}
                                disabled={scheduleSubmitting}
                                sx={{
                                    fontFamily, fontSize: '0.75rem', textTransform: 'none', fontWeight: 600,
                                    borderRadius: '8px',
                                    background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                                    '&:hover': { background: 'linear-gradient(135deg, #6d28d9, #8b5cf6)' },
                                }}
                            >
                                {scheduleSubmitting ? 'Schedulingâ€¦' : 'Schedule'}
                            </Button>
                        </Box>
                    </Popover>
                </Box>
            </Box>

            {/* Create Task Dialog */}
            <Dialog
                open={createTaskOpen}
                onClose={handleCloseCreateTask}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        bgcolor: 'rgba(255,255,255,0.98)',
                        backdropFilter: 'blur(12px)',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                        overflow: 'hidden',
                    },
                }}
            >
                <DialogContent sx={{ p: 0 }}>
                    {/* Dialog Header */}
                    <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        px: 3, py: 2, borderBottom: '1px solid rgba(0,0,0,0.06)',
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{
                                width: 32, height: 32, borderRadius: '10px',
                                background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <AutoAwesomeIcon sx={{ color: '#fff', fontSize: 18 }} />
                            </Box>
                            <Typography sx={{ fontFamily, fontSize: '0.95rem', fontWeight: 700, color: colors.textPrimary }}>
                                {wizardStep === 1 ? 'Create Task' : wizardStep === 2 ? (isConnectionAgent ? 'Connect Account' : 'Agent Info') : (isConnectionAgent ? 'Prompt Settings' : 'Select Resources')}
                            </Typography>
                        </Box>
                        <IconButton size="small" onClick={handleCloseCreateTask} sx={{ color: colors.textTertiary }}>
                            <CloseIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                    </Box>

                    {/* Dialog Body */}
                    <Box sx={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        gap: 2.5, p: { xs: 2, sm: 3 }, minHeight: 200,
                    }}>
                        {/* ===== Step 1: Pick an Agent ===== */}
                        {wizardStep === 1 && (
                            <>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography sx={{ fontFamily, fontSize: '0.8rem', color: colors.textTertiary, maxWidth: 400 }}>
                                        Select an agent type to get started
                                    </Typography>
                                </Box>
                                {loadingAgentTypes ? (
                                    <CircularProgress size={28} sx={{ color: colors.accent }} />
                                ) : (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1, sm: 1.5 }, justifyContent: 'center', maxWidth: 520, px: { xs: 1, sm: 0 } }}>
                                        {agentTypes.map(agent => {
                                            const connKey = agent.connection_type || agent.id;
                                            const conn = agentSpaceConnections[connKey];
                                            const needsConnection = agent.requires_connection && !conn?.is_connected;
                                            return (
                                                <Box
                                                    key={agent.id}
                                                    onClick={() => handleSelectAgent(agent.id)}
                                                    sx={{
                                                        display: 'flex', alignItems: 'center', gap: 1,
                                                        px: 2, py: 1.25, borderRadius: '12px', cursor: 'pointer',
                                                        bgcolor: selectedAgentType === agent.id ? `${agent.color}10` : 'rgba(255,255,255,0.8)',
                                                        border: `1.5px solid ${selectedAgentType === agent.id ? agent.color : 'rgba(0,0,0,0.08)'}`,
                                                        transition: 'all 0.2s ease',
                                                        position: 'relative',
                                                        '&:hover': {
                                                            bgcolor: `${agent.color}10`,
                                                            border: `1.5px solid ${agent.color}60`,
                                                            transform: 'translateY(-1px)',
                                                        },
                                                    }}
                                                >
                                                    <Box sx={{ color: agent.color, display: 'flex' }}>
                                                        {getIcon(agent.icon_name, 20)}
                                                    </Box>
                                                    <Box>
                                                        <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, color: colors.textPrimary }}>
                                                            {agent.label}
                                                        </Typography>
                                                        <Typography sx={{ fontFamily, fontSize: '0.65rem', color: colors.textTertiary }}>
                                                            {agent.description}
                                                        </Typography>
                                                    </Box>
                                                    {needsConnection && (
                                                        <Box sx={{
                                                            position: 'absolute', top: -4, right: -4,
                                                            bgcolor: '#f59e0b', color: '#fff',
                                                            fontSize: '0.5rem', fontWeight: 700, fontFamily,
                                                            px: 0.75, py: 0.25, borderRadius: '6px',
                                                            lineHeight: 1,
                                                        }}>
                                                            Not connected
                                                        </Box>
                                                    )}
                                                    {conn?.is_connected && (
                                                        <Box sx={{
                                                            position: 'absolute', top: -4, right: -4,
                                                            bgcolor: colors.success, color: '#fff',
                                                            fontSize: '0.5rem', fontWeight: 700, fontFamily,
                                                            px: 0.75, py: 0.25, borderRadius: '6px',
                                                            lineHeight: 1,
                                                        }}>
                                                            Connected
                                                        </Box>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                )}
                            </>
                        )}

                        {/* ===== Step 2: Connection Step (for gmail/outlook/calendar) ===== */}
                        {wizardStep === 2 && isConnectionAgent && agentInfo && (
                            <>
                                <BackButton onClick={handleBackToAgents} label="Back to agents" />
                                <AgentBadge />

                                <Box sx={{
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5,
                                    p: { xs: 2, sm: 2.5 }, borderRadius: { xs: '10px', sm: '14px' },
                                    bgcolor: 'rgba(255,255,255,0.8)',
                                    border: `1.5px ${isAgentConnected ? 'solid' : 'dashed'} ${isAgentConnected ? colors.success + '60' : 'rgba(0,0,0,0.12)'}`,
                                    maxWidth: { xs: '100%', sm: 420 },
                                    width: '100%',
                                }}>
                                    {isAgentConnected ? (
                                        <>
                                            <CheckCircleIcon sx={{ fontSize: 32, color: colors.success }} />
                                            <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: colors.success, textAlign: 'center' }}>
                                                Connected to {agentInfo.label}
                                            </Typography>
                                            {connectionData?.connected_email && (
                                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: colors.textSecondary, textAlign: 'center' }}>
                                                    {connectionData.connected_email}
                                                </Typography>
                                            )}
                                            <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5 }}>
                                                <Box
                                                    onClick={() => { setWizardStep(3); }}
                                                    sx={{
                                                        fontFamily, fontSize: '0.8rem', fontWeight: 600,
                                                        color: '#fff', cursor: 'pointer',
                                                        px: 3, py: 1, borderRadius: '10px',
                                                        background: `linear-gradient(135deg, ${agentInfo.color}, ${agentInfo.color}cc)`,
                                                        boxShadow: `0 4px 12px ${agentInfo.color}30`,
                                                        transition: 'all 0.2s ease',
                                                        '&:hover': { transform: 'translateY(-1px)', boxShadow: `0 6px 16px ${agentInfo.color}40` },
                                                    }}
                                                >
                                                    Next
                                                </Box>
                                                <Box
                                                    onClick={() => !disconnectingAgent && handleDisconnect(resolvedConnectionType)}
                                                    sx={{
                                                        fontFamily, fontSize: '0.75rem', fontWeight: 500,
                                                        color: colors.danger, cursor: disconnectingAgent ? 'default' : 'pointer',
                                                        px: 2, py: 1, borderRadius: '10px',
                                                        border: `1px solid ${colors.danger}40`,
                                                        opacity: disconnectingAgent ? 0.6 : 1,
                                                        transition: 'all 0.2s ease',
                                                        '&:hover': { bgcolor: `${colors.danger}08` },
                                                    }}
                                                >
                                                    {disconnectingAgent ? 'Disconnecting...' : 'Disconnect'}
                                                </Box>
                                            </Box>
                                        </>
                                    ) : (
                                        <>
                                            <LinkIcon sx={{ fontSize: 32, color: colors.textTertiary }} />
                                            <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: colors.textPrimary, textAlign: 'center' }}>
                                                Connect your {agentInfo.label} account
                                            </Typography>
                                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: colors.textTertiary, textAlign: 'center' }}>
                                                Connect to start using the {agentInfo.label} agent in Agent Space
                                            </Typography>
                                            <Box
                                                onClick={() => !connectingAgent && handleConnect(selectedAgentType)}
                                                sx={{
                                                    fontFamily, fontSize: '0.8rem', fontWeight: 600,
                                                    color: '#fff', cursor: connectingAgent ? 'default' : 'pointer',
                                                    px: 3, py: 1, borderRadius: '10px',
                                                    background: `linear-gradient(135deg, ${agentInfo.color}, ${agentInfo.color}cc)`,
                                                    boxShadow: `0 4px 12px ${agentInfo.color}30`,
                                                    opacity: connectingAgent ? 0.7 : 1,
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': { transform: connectingAgent ? 'none' : 'translateY(-1px)', boxShadow: `0 6px 16px ${agentInfo.color}40` },
                                                }}
                                            >
                                                {connectingAgent ? 'Connecting...' : `Connect ${agentInfo.label}`}
                                            </Box>
                                        </>
                                    )}
                                </Box>
                            </>
                        )}

                        {/* ===== Step 2: Agent Info (for non-connection agents) ===== */}
                        {wizardStep === 2 && !isConnectionAgent && agentInfo && (
                            <>
                                <BackButton onClick={handleBackToAgents} label="Back to agents" />
                                <AgentBadge />

                                {isAgentWithResources(selectedAgentType) ? (
                                    <Box sx={{ textAlign: 'center' }}>
                                        {loadingResources ? (
                                            <CircularProgress size={20} sx={{ color: agentInfo.color }} />
                                        ) : availableResources.length > 0 ? (
                                            <Typography
                                                onClick={() => setWizardStep(3)}
                                                sx={{
                                                    fontFamily, fontSize: '0.8rem', fontWeight: 600,
                                                    color: agentInfo.color, cursor: 'pointer',
                                                    px: 2.5, py: 1, borderRadius: '10px',
                                                    border: `1.5px solid ${agentInfo.color}`,
                                                    bgcolor: `${agentInfo.color}08`,
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': { bgcolor: `${agentInfo.color}15` },
                                                }}
                                            >
                                                Select resources ({availableResources.length} available)
                                            </Typography>
                                        ) : (
                                            <>
                                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: colors.textTertiary }}>
                                                    No resources found. Close and type your task below.
                                                </Typography>
                                                <Box
                                                    onClick={handleCloseCreateTask}
                                                    sx={{
                                                        fontFamily, fontSize: '0.8rem', fontWeight: 600,
                                                        color: '#fff', cursor: 'pointer', mt: 1,
                                                        px: 3, py: 1, borderRadius: '10px',
                                                        background: `linear-gradient(135deg, ${agentInfo.color}, ${agentInfo.color}cc)`,
                                                        transition: 'all 0.2s ease',
                                                        '&:hover': { transform: 'translateY(-1px)' },
                                                    }}
                                                >
                                                    Start Typing
                                                </Box>
                                            </>
                                        )}
                                    </Box>
                                ) : (
                                    <>
                                        <Typography sx={{ fontFamily, fontSize: '0.75rem', color: colors.textTertiary }}>
                                            Close and type your task below to get started
                                        </Typography>
                                        <Box
                                            onClick={handleCloseCreateTask}
                                            sx={{
                                                fontFamily, fontSize: '0.8rem', fontWeight: 600,
                                                color: '#fff', cursor: 'pointer',
                                                px: 3, py: 1, borderRadius: '10px',
                                                background: `linear-gradient(135deg, ${agentInfo.color}, ${agentInfo.color}cc)`,
                                                transition: 'all 0.2s ease',
                                                '&:hover': { transform: 'translateY(-1px)' },
                                            }}
                                        >
                                            Start Typing
                                        </Box>
                                    </>
                                )}
                            </>
                        )}

                        {/* ===== Step 3: Prompt Settings (for connection agents) ===== */}
                        {wizardStep === 3 && isConnectionAgent && agentInfo && (
                            <>
                                <BackButton onClick={() => setWizardStep(2)} label="Back to connection" />

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip
                                        icon={<Box sx={{ color: 'inherit', display: 'flex' }}>{getIcon(agentInfo.icon_name, 16)}</Box>}
                                        label={agentInfo.label}
                                        size="small"
                                        sx={{
                                            fontFamily, fontSize: '0.75rem', fontWeight: 600, height: 30,
                                            bgcolor: `${agentInfo.color}15`,
                                            border: `1px solid ${agentInfo.color}`,
                                            color: agentInfo.color,
                                        }}
                                    />
                                    <Chip
                                        icon={<SettingsIcon sx={{ fontSize: 14 }} />}
                                        label="Prompt Settings"
                                        size="small"
                                        sx={{
                                            fontFamily, fontSize: '0.7rem', fontWeight: 500, height: 28,
                                            bgcolor: 'rgba(0,0,0,0.04)',
                                            border: '1px solid rgba(0,0,0,0.08)',
                                            color: colors.textSecondary,
                                        }}
                                    />
                                </Box>

                                {loadingPrompt ? (
                                    <CircularProgress size={24} sx={{ color: agentInfo.color }} />
                                ) : (
                                    <Box sx={{
                                        width: '100%', maxWidth: { xs: '100%', sm: 560 },
                                        bgcolor: 'rgba(255,255,255,0.7)',
                                        borderRadius: { xs: '10px', sm: '14px' },
                                        border: `1px solid ${agentInfo.color}20`,
                                        p: { xs: 1.5, sm: 2 },
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, color: colors.textPrimary }}>
                                                Agent Prompt
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 0.75 }}>
                                                {isDefaultPrompt ? (
                                                    <Chip
                                                        label="Default"
                                                        size="small"
                                                        sx={{
                                                            fontFamily, fontSize: '0.6rem', fontWeight: 600, height: 22,
                                                            bgcolor: `${colors.success}15`,
                                                            color: colors.success,
                                                            border: `1px solid ${colors.success}40`,
                                                        }}
                                                    />
                                                ) : (
                                                    <Chip
                                                        label="Custom"
                                                        size="small"
                                                        sx={{
                                                            fontFamily, fontSize: '0.6rem', fontWeight: 600, height: 22,
                                                            bgcolor: `${agentInfo.color}15`,
                                                            color: agentInfo.color,
                                                            border: `1px solid ${agentInfo.color}40`,
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </Box>

                                        {promptEditing ? (
                                            <>
                                                <textarea
                                                    value={agentPrompt}
                                                    onChange={(e) => setAgentPrompt(e.target.value)}
                                                    rows={8}
                                                    style={{
                                                        width: '100%', border: `1px solid ${agentInfo.color}40`,
                                                        borderRadius: '10px', padding: '12px',
                                                        fontFamily, fontSize: '0.75rem', lineHeight: '1.6',
                                                        color: colors.textPrimary, backgroundColor: 'rgba(255,255,255,0.9)',
                                                        outline: 'none', resize: 'vertical', minHeight: 120,
                                                    }}
                                                />
                                                <Box sx={{ display: 'flex', gap: 1, mt: 1.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                                    {!isDefaultPrompt && (
                                                        <Box
                                                            onClick={() => !savingPrompt && handleResetPrompt()}
                                                            sx={{
                                                                display: 'flex', alignItems: 'center', gap: 0.5,
                                                                fontFamily, fontSize: '0.7rem', fontWeight: 500,
                                                                color: colors.textTertiary, cursor: savingPrompt ? 'default' : 'pointer',
                                                                px: 1.5, py: 0.75, borderRadius: '8px',
                                                                border: '1px solid rgba(0,0,0,0.1)',
                                                                transition: 'all 0.2s',
                                                                '&:hover': { color: colors.danger, borderColor: `${colors.danger}40` },
                                                            }}
                                                        >
                                                            <RestartAltIcon sx={{ fontSize: 14 }} />
                                                            Reset to Default
                                                        </Box>
                                                    )}
                                                    <Box
                                                        onClick={() => { setPromptEditing(false); setAgentPrompt(isDefaultPrompt ? defaultPrompt : agentPrompt); }}
                                                        sx={{
                                                            fontFamily, fontSize: '0.7rem', fontWeight: 500,
                                                            color: colors.textSecondary, cursor: 'pointer',
                                                            px: 1.5, py: 0.75, borderRadius: '8px',
                                                            border: '1px solid rgba(0,0,0,0.1)',
                                                            transition: 'all 0.2s',
                                                            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                                                        }}
                                                    >
                                                        Cancel
                                                    </Box>
                                                    <Box
                                                        onClick={() => !savingPrompt && handleSavePrompt()}
                                                        sx={{
                                                            fontFamily, fontSize: '0.7rem', fontWeight: 600,
                                                            color: '#fff', cursor: savingPrompt ? 'default' : 'pointer',
                                                            px: 2, py: 0.75, borderRadius: '8px',
                                                            background: `linear-gradient(135deg, ${agentInfo.color}, ${agentInfo.color}cc)`,
                                                            opacity: savingPrompt ? 0.7 : 1,
                                                            transition: 'all 0.2s',
                                                            '&:hover': { transform: savingPrompt ? 'none' : 'translateY(-1px)' },
                                                        }}
                                                    >
                                                        {savingPrompt ? 'Saving...' : 'Save Prompt'}
                                                    </Box>
                                                </Box>
                                            </>
                                        ) : (
                                            <>
                                                <Box sx={{
                                                    p: 1.5, borderRadius: '10px',
                                                    bgcolor: 'rgba(0,0,0,0.02)',
                                                    border: '1px solid rgba(0,0,0,0.06)',
                                                    maxHeight: 140, overflow: 'auto',
                                                    '&::-webkit-scrollbar': { width: '4px' },
                                                    '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.1)', borderRadius: '4px' },
                                                }}>
                                                    <Typography sx={{
                                                        fontFamily, fontSize: '0.7rem', color: colors.textSecondary,
                                                        lineHeight: '1.6', whiteSpace: 'pre-wrap',
                                                    }}>
                                                        {agentPrompt.length > 300 ? agentPrompt.substring(0, 300) + '...' : agentPrompt}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 1, mt: 1.5, justifyContent: 'flex-end' }}>
                                                    <Box
                                                        onClick={() => setPromptEditing(true)}
                                                        sx={{
                                                            display: 'flex', alignItems: 'center', gap: 0.5,
                                                            fontFamily, fontSize: '0.7rem', fontWeight: 500,
                                                            color: agentInfo.color, cursor: 'pointer',
                                                            px: 1.5, py: 0.75, borderRadius: '8px',
                                                            border: `1px solid ${agentInfo.color}40`,
                                                            transition: 'all 0.2s',
                                                            '&:hover': { bgcolor: `${agentInfo.color}08` },
                                                        }}
                                                    >
                                                        <EditIcon sx={{ fontSize: 14 }} />
                                                        Edit Prompt
                                                    </Box>
                                                </Box>
                                            </>
                                        )}
                                    </Box>
                                )}

                                {!promptEditing && (
                                    <Box
                                        onClick={handleCloseCreateTask}
                                        sx={{
                                            fontFamily, fontSize: '0.8rem', fontWeight: 600,
                                            color: '#fff', cursor: 'pointer',
                                            px: 3, py: 1, borderRadius: '10px',
                                            background: `linear-gradient(135deg, ${agentInfo.color}, ${agentInfo.color}cc)`,
                                            transition: 'all 0.2s ease',
                                            '&:hover': { transform: 'translateY(-1px)' },
                                        }}
                                    >
                                        Start Typing
                                    </Box>
                                )}
                            </>
                        )}

                        {/* ===== Step 3: Resource Selection (for non-connection agents) ===== */}
                        {wizardStep === 3 && !isConnectionAgent && agentInfo && (
                            <>
                                <BackButton onClick={() => setWizardStep(2)} />

                                <Chip
                                    icon={<Box sx={{ color: 'inherit', display: 'flex' }}>{getIcon(agentInfo.icon_name, 16)}</Box>}
                                    label={agentInfo.label}
                                    size="small"
                                    sx={{
                                        fontFamily, fontSize: '0.75rem', fontWeight: 600, height: 30,
                                        bgcolor: `${agentInfo.color}15`,
                                        border: `1px solid ${agentInfo.color}`,
                                        color: agentInfo.color,
                                    }}
                                />

                                <Box sx={{
                                    width: '100%', maxWidth: { xs: '100%', sm: 520 },
                                    bgcolor: 'rgba(255,255,255,0.7)',
                                    borderRadius: { xs: '10px', sm: '14px' },
                                    border: `1px solid ${agentInfo.color}20`,
                                    p: { xs: 1.5, sm: 2 },
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                        <Typography sx={{
                                            fontFamily, fontSize: '0.75rem', fontWeight: 600,
                                            color: agentInfo.color,
                                        }}>
                                            Select Resources
                                        </Typography>
                                        {selectedResources.length > 0 && (
                                            <Typography sx={{ fontFamily, fontSize: '0.65rem', color: colors.textTertiary }}>
                                                {selectedResources.length} selected
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box sx={{
                                        display: 'flex', flexWrap: 'wrap', gap: 0.75,
                                        maxHeight: 200, overflow: 'auto',
                                        '&::-webkit-scrollbar': { width: '4px' },
                                        '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.1)', borderRadius: '4px' },
                                    }}>
                                        {availableResources.map((resource) => {
                                            const isSelected = selectedResources.some(r => r.id === resource.id && r.type === (resource.resourceType || resource.type));
                                            return (
                                                <Chip
                                                    key={`${resource.id}-${resource.resourceType || ''}`}
                                                    label={resource.label}
                                                    onClick={() => handleToggleResource(resource, true)}
                                                    size="small"
                                                    sx={{
                                                        fontFamily, fontSize: '0.7rem', height: 28,
                                                        bgcolor: isSelected ? `${agentInfo.color}15` : 'rgba(255,255,255,0.9)',
                                                        border: `1px solid ${isSelected ? agentInfo.color : 'rgba(0,0,0,0.1)'}`,
                                                        color: isSelected ? agentInfo.color : colors.textSecondary,
                                                        fontWeight: isSelected ? 600 : 400,
                                                        '&:hover': { bgcolor: `${agentInfo.color}10` },
                                                    }}
                                                />
                                            );
                                        })}
                                    </Box>
                                </Box>

                                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: colors.textTertiary, textAlign: 'center' }}>
                                    Click a resource to select it and return to the message area
                                </Typography>
                            </>
                        )}
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ fontFamily, borderRadius: '12px' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
